/**
 * Offer, seller-listing and disposition form conditions.
 *
 * A hidden conditional field must also stop participating in native validation
 * and form submission. These hooks are scoped to the offer, listing and disposition forms so
 * ordinary sheet and collapse visibility never clears a value.
 */

function controls(container) {
    return container.querySelectorAll('input, select, textarea');
}

function clearControl(control) {
    if (control.type === 'radio' || control.type === 'checkbox') {
        control.checked = false;
    } else {
        control.value = '';
    }
}

function setRequired(container, active) {
    var names = (container.getAttribute('data-condition-required') || '').split(',').filter(Boolean);
    names.forEach(function (name) {
        container.querySelectorAll('[name="' + name + '"]').forEach(function (control) {
            control.required = active;
        });
    });
}

function setConditional(container, active, clearOnHide) {
    container.hidden = !active;
    controls(container).forEach(function (control) {
        // Nested inline-create fields own their disabled lifecycle. Re-enabling
        // them here would make a closed mini-form join the parent submission.
        if (!control.hasAttribute('data-inline-create-control')) {
            control.disabled = !active;
        }
        if (!active && clearOnHide) clearControl(control);
    });
    setRequired(container, active);
}

function offerSigned(form) {
    return !!form.querySelector('input[name="offer_status"][value="accepted"]:checked');
}

// Generate contract PDF is offered only on Not signed (`AcqOfferCreateForm`).
function generatingContract(form) {
    if (offerSigned(form)) return false;
    var generate = form.querySelector('input[name="generate_contract"]');
    return !!generate && generate.checked;
}

function assignmentPurchase(form) {
    return !!form.querySelector('input[name="purchase_type"][value="assignment"]:checked');
}

// Wholesaler's price: Disclosed shows the fee.  Unpicked counts as Not disclosed.
function showingAssignmentFee(form) {
    return !!form.querySelector('input[name="show_assignment_fee"][value="True"]:checked');
}

// The requirement sets in force: `signed` for a Signed contract, `generate`
// when the PDF will be printed, plus `generate-direct` or `generate-assignment`
// for the purchase type's own document (and `generate-assignment-fee` when
// Wholesaler's price is Disclosed), and `assignment` on any Assignment purchase. Each
// `data-required-modes` lists the sets that require its field
// (`AcqOfferCreateForm.clean`).
function activeRequirements(form) {
    var assignment = assignmentPurchase(form);
    var modes = assignment ? ['assignment'] : [];
    if (offerSigned(form)) return modes.concat(['signed']);
    if (!generatingContract(form)) return modes;
    modes.push('generate', assignment ? 'generate-assignment' : 'generate-direct');
    if (assignment && showingAssignmentFee(form)) modes.push('generate-assignment-fee');
    return modes;
}

function syncContract(form, clearOnHide) {
    var status = offerSigned(form) ? 'accepted' : 'to_submit';
    // `data-for-form` reaches blocks outside the form, like the Create Offer
    // sheet's Generate as-is in the sheet footer.
    var blocks = Array.prototype.slice.call(form.querySelectorAll('[data-offer-status-show]'));
    if (form.id) {
        blocks = blocks.concat(Array.prototype.slice.call(
            document.querySelectorAll('[data-offer-status-show][data-for-form="' + form.id + '"]')
        ));
    }
    blocks.forEach(function (block) {
        setConditional(block, block.getAttribute('data-offer-status-show') === status, false);
    });
    // Generate as-is lists the empty fields of the purchase type's own document.
    var asIsKind = 'direct';
    if (assignmentPurchase(form)) asIsKind = showingAssignmentFee(form) ? 'assignment-fee' : 'assignment';
    if (form.id) {
        document.querySelectorAll('button[data-pdf-download-as-is][form="' + form.id + '"]').forEach(function (button) {
            var fields = button.getAttribute('data-pdf-download-as-is-fields-' + asIsKind);
            if (fields !== null) button.setAttribute('data-pdf-download-as-is-fields', fields);
        });
    }
    // On Signed, uploads start as Signed Contract, and rows already queued as Contract
    // move to it (and back on Not signed, where Signed Contract would file as Contract).
    // A row set to any other type keeps it.
    form.querySelectorAll('[data-queue-add-url-' + status + ']').forEach(function (card) {
        card.setAttribute('data-queue-add-url', card.getAttribute('data-queue-add-url-' + status));
        var from = status === 'accepted' ? 'contract' : 'executed_contract';
        var to = status === 'accepted' ? 'executed_contract' : 'contract';
        card.querySelectorAll('[data-file-type]').forEach(function (select) {
            if (select.value !== from || !select.querySelector('option[value="' + to + '"]')) return;
            select.value = to;
            select.dispatchEvent(new Event('change', {bubbles: true}));
        });
    });

    // Due diligence days show on Yes.  An Assignment purchase hides the whole due diligence
    // block (`data-show-when`), so the days stay hidden, disabled and not required there, and
    // keep what was typed for a switch back to Direct.
    var assignment = assignmentPurchase(form);
    var dueDiligence = !assignment && !!form.querySelector('input[name="due_diligence"][value="true"]:checked');
    form.querySelectorAll('[data-dd-detail]').forEach(function (detail) {
        setConditional(detail, dueDiligence, clearOnHide && !assignment);
    });

    var entity = form.querySelector('input[name="buying_entity"]:checked');
    form.querySelectorAll('[data-signer-entity]').forEach(function (signer) {
        var matchingEntity = entity && signer.getAttribute('data-signer-entity') === entity.value;
        setConditional(signer, matchingEntity, clearOnHide);
    });

    var active = activeRequirements(form);
    form.querySelectorAll('[data-required-modes]').forEach(function (detail) {
        var modes = detail.getAttribute('data-required-modes').split(' ');
        var required = modes.some(function (mode) { return active.indexOf(mode) !== -1; });
        setRequired(detail, required && !detail.hidden);
    });
    syncContractRequirements(form);
}

// The post-closing possession rule protects the purchase agreement's section 7,
// which an assignment does not print.
function syncContractRequirements(form) {
    var generating = generatingContract(form) && !assignmentPurchase(form);
    var postClosing = !!form.querySelector('input[name="seller_possession"][value="post_closing"]:checked');
    form.querySelectorAll('[data-contract-special-stipulations]').forEach(function (detail) {
        setRequired(detail, generating && postClosing);
    });
}

function selectDefaultSigner(form, entity) {
    form.querySelectorAll('input[name="buying_entity_signer"]').forEach(function (signer) {
        signer.checked = false;
    });
    var selector = '[data-signer-entity="' + CSS.escape(entity.value) + '"][data-signer-default] input';
    var preferred = form.querySelector(selector);
    if (preferred) preferred.checked = true;
}

function syncPossession(form, clearOnHide) {
    var postClosing = !!form.querySelector('input[name="seller_possession"][value="post_closing"]:checked');
    form.querySelectorAll('[data-offer-possession-details]').forEach(function (detail) {
        setConditional(detail, postClosing, clearOnHide);
    });
}

// Disposition sheet: each At closing detail lists the `at_closing` values that show it
// (`AT_CLOSING_DETAIL_SHOW_ON` in `acqs/forms/dispositions.py`).
function syncAtClosing(form, clearOnHide) {
    var choice = form.querySelector('input[name="at_closing"]:checked');
    form.querySelectorAll('[data-at-closing-show-on]').forEach(function (detail) {
        var showOn = detail.getAttribute('data-at-closing-show-on').split(' ');
        setConditional(detail, !!choice && showOn.indexOf(choice.value) !== -1, clearOnHide);
    });
}

function syncNextAction(form, clearOnHide) {
    var fields = form.querySelector('[data-offer-next-action-fields]');
    if (!fields) return;
    setConditional(fields, fields.classList.contains('collapse--open'), clearOnHide);
}

function closeHoaInlineCreate(fields) {
    fields.querySelectorAll('[data-inline-create-form]').forEach(function (panel) {
        var cancel = panel.querySelector('[data-inline-create-cancel]');
        if (cancel) cancel.click();
        panel.querySelectorAll('[data-inline-create-control]').forEach(function (control) {
            control.disabled = true;
            clearControl(control);
        });
    });
}

// The seller listing's HOA picker and dues: each is its own
// `data-listing-hoa-fields` block, shown while the HOA toggle's Yes is picked.
function syncHoa(form, clearOnHide) {
    var picked = form.querySelector('[data-listing-has-hoa]:checked');
    var active = !!picked && picked.value === 'yes';
    form.querySelectorAll('[data-listing-hoa-fields]').forEach(function (fields) {
        if (!active) closeHoaInlineCreate(fields);
        setConditional(fields, active, clearOnHide);
    });
}

// Landing-stage picks: the seller-listing cancel's radios, or the offer page's
// Cancel contract select. Each terminal block names its stage, and only the
// picked one stays enabled, so its reason is the one that posts. A
// `data-listing-fallback-open` block is the inverse: it shows only while the
// pick keeps the pursuit open, and keeps its values when hidden.
function syncListingFallback(form, clearOnHide) {
    var picker = form.querySelector('select[data-listing-fallback-stage]') ||
        form.querySelector('[data-listing-fallback-stage]:checked');
    var closing = false;
    form.querySelectorAll('[data-listing-fallback-terminal]').forEach(function (terminal) {
        var picked = !!picker && picker.value === terminal.getAttribute('data-terminal-stage');
        if (picked) closing = true;
        setConditional(terminal, picked, clearOnHide);
    });
    form.querySelectorAll('[data-listing-fallback-open]').forEach(function (block) {
        setConditional(block, !closing, false);
    });
}

function initialize(root) {
    var forms = new Set(root.querySelectorAll('form'));
    var parentForm = root.closest && root.closest('form');
    if (parentForm) forms.add(parentForm);
    forms.forEach(function (form) {
        if (form.id === 'create-offer-form') {
            syncContract(form, false);
            syncNextAction(form, false);
        }
        if (form.querySelector('[data-offer-possession-details]')) syncPossession(form, false);
        if (form.querySelector('[data-at-closing-show-on]')) syncAtClosing(form, false);
        if (form.querySelector('[data-listing-has-hoa]')) syncHoa(form, false);
        if (form.querySelector('[data-listing-fallback-terminal]')) syncListingFallback(form, false);
    });
}

document.addEventListener('change', function (event) {
    var form = event.target.closest && event.target.closest('form');
    if (!form) return;
    if (form.id === 'create-offer-form' && event.target.matches('input[name="offer_status"], input[name="generate_contract"], input[name="due_diligence"], input[name="purchase_type"], input[name="show_assignment_fee"]')) {
        syncContract(form, true);
        // The first pick shows the form (`data-show-when="purchase_type"`), and show-when
        // re-enables every field it disabled, including the collapsed next-action fields and
        // the hidden possession details.  It registers its change listener first, so these
        // run after it and disable them again.
        if (event.target.name === 'purchase_type') {
            syncNextAction(form, false);
            syncPossession(form, false);
        }
    } else if (form.id === 'create-offer-form' && event.target.matches('input[name="buying_entity"]')) {
        selectDefaultSigner(form, event.target);
        syncContract(form, true);
    } else if (form.querySelector('[data-offer-possession-details]') && event.target.matches('input[name="seller_possession"]')) {
        syncPossession(form, true);
        syncContractRequirements(form);
    } else if (form.querySelector('[data-at-closing-show-on]') && event.target.matches('input[name="at_closing"]')) {
        syncAtClosing(form, true);
    } else if (event.target.matches('[data-listing-has-hoa]')) {
        syncHoa(form, true);
    } else if (event.target.matches('[data-listing-fallback-stage]')) {
        syncListingFallback(form, true);
    }
});

document.addEventListener('click', function (event) {
    var cancel = event.target.closest && event.target.closest('[data-inline-create-cancel]');
    if (cancel) {
        var panel = cancel.closest('[data-inline-create-form]');
        var owner = cancel.closest('form');
        if (panel && owner && (owner.querySelector('[data-offer-possession-details]') ||
                owner.querySelector('[data-listing-has-hoa]'))) {
            controls(panel).forEach(clearControl);
        }
    }
    var toggle = event.target.closest && event.target.closest('[data-offer-next-action-toggle]');
    if (!toggle) return;
    var form = toggle.closest('form');
    if (form) syncNextAction(form, true);
});

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initialize(document); });
} else {
    initialize(document);
}
document.addEventListener('htmx:afterSettle', function (event) { initialize(event.target); });
document.addEventListener('sheet:ready', function (event) { initialize(event.target); });

// Disposition form: "Copy from underwriting" fills the Value card's ARV and rehab inputs and the
// featured-level radio from the calculator values the view put on the button. Nothing saves
// until staff save the form.
document.addEventListener('click', function (event) {
    var button = event.target.closest && event.target.closest('[data-copy-underwriting]');
    if (!button) return;
    var form = button.closest('form');
    if (!form) return;
    var values = JSON.parse(button.getAttribute('data-copy-underwriting') || '{}');
    Object.keys(values).forEach(function (name) {
        if (name === 'featured_level') {
            var radio = form.querySelector('input[name="featured_level"][value="' + values[name] + '"]');
            if (radio) radio.checked = true;
            return;
        }
        var input = form.querySelector('input[name="' + name + '"]');
        if (input) {
            input.value = values[name];
            input.dispatchEvent(new Event('input', { bubbles: true }));
        }
    });
});
